import React, { useState } from 'react';
import { Label } from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import {
  InfraService,
  PreviewService,
  MirrordOverrides,
  DbBranchEntry,
  DbBranchUiType,
  DbBranchParamValue,
  DbBranchAppMetaOverride,
  KafkaSplitEntry,
  KafkaTopicCatalogEntry,
} from '../helpers/apiHelpers';
import {
  DB_FIELDS,
  DB_DEFAULT_VERSIONS,
  matchEnvVarsToDbFields,
  resolveDbBranchName,
} from '../../../../shared/previewEnv/matchEnvVarsToDbFields';

// ---------------------------------------------------------------------------
// Colour palette matching mockup
// ---------------------------------------------------------------------------
export const C = {
  purple: '#6554C0',
  purpleBg: '#EAE6FF',
  blue: '#0052CC',
  blueBg: '#DEEBFF',
  green: '#00875A',
  greenBg: '#E3FCEF',
  kafkaBrown: '#7d5700',
  kafkaBg: '#fdf4e7',
  muted: '#5E6C84',
  border: '#DFE1E6',
  bg: '#F4F5F7',
};

export const badge = (color: string, bg: string): React.CSSProperties => ({
  display: 'inline-block',
  background: bg,
  color,
  fontSize: 11,
  fontWeight: 700,
  padding: '1px 7px',
  borderRadius: 10,
  marginLeft: 4,
});

export const changedBadge: React.CSSProperties = {
  display: 'inline-block',
  background: C.purpleBg,
  color: C.purple,
  fontSize: 11,
  fontWeight: 700,
  padding: '1px 7px',
  borderRadius: 10,
  marginLeft: 4,
};

const formLabel: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: C.muted, marginBottom: 4 };

const inputBase: React.CSSProperties = {
  width: '100%',
  padding: '4px 8px',
  border: `1px solid ${C.border}`,
  borderRadius: 4,
  fontSize: 13,
  boxSizing: 'border-box',
};

const inputModified: React.CSSProperties = {
  ...inputBase,
  border: `1px solid ${C.purple}`,
  background: '#FEFEFE',
};

const selectBase: React.CSSProperties = { ...inputBase, background: '#fff', cursor: 'pointer' };
const selectModified: React.CSSProperties = { ...inputModified, background: '#fff', cursor: 'pointer' };

const toggleTrack = (on: boolean): React.CSSProperties => ({
  display: 'inline-flex',
  width: 36,
  height: 20,
  borderRadius: 10,
  background: on ? C.blue : '#C1C7D0',
  cursor: 'pointer',
  alignItems: 'center',
  padding: '0 3px',
  flexShrink: 0,
  transition: 'background 0.15s',
});

const toggleKnob = (on: boolean): React.CSSProperties => ({
  width: 14,
  height: 14,
  borderRadius: '50%',
  background: '#fff',
  transform: on ? 'translateX(16px)' : 'translateX(0)',
  transition: 'transform 0.15s',
});

// ---------------------------------------------------------------------------
// countOverrides helper
// ---------------------------------------------------------------------------
export function countOverrides(ov: MirrordOverrides | undefined) {
  if (!ov?.feature) return { total: 0, env: false, db: false, kafka: false };
  const env = Object.keys(ov.feature.env?.override ?? {}).length > 0;
  const db = (ov.feature.db_branches?.length ?? 0) > 0;
  const kafka = Object.keys(ov.feature.split_queues ?? {}).length > 0;
  return { total: (env ? 1 : 0) + (db ? 1 : 0) + (kafka ? 1 : 0), env, db, kafka };
}

// ---------------------------------------------------------------------------
// AccordionSection
// ---------------------------------------------------------------------------
const AccordionSection: React.FC<{
  open: boolean;
  onToggle: () => void;
  title: string;
  tag?: React.ReactNode;
  last?: boolean;
  children: React.ReactNode;
}> = ({ open, onToggle, title, tag, last, children }) => (
  <div style={{ borderBottom: last ? 'none' : `1px solid ${C.border}` }}>
    <div
      onClick={onToggle}
      style={{
        padding: '8px 14px',
        background: C.bg,
        borderBottom: open ? `1px solid ${C.border}` : 'none',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 13,
        fontWeight: 600,
        color: '#172B4D',
        cursor: 'pointer',
        userSelect: 'none',
      }}
    >
      <span style={{ fontSize: 11, color: C.muted, minWidth: 12 }}>{open ? '▼' : '▶'}</span>
      {title}
      {tag}
    </div>
    {open && <div style={{ padding: '12px 16px' }}>{children}</div>}
  </div>
);

// ---------------------------------------------------------------------------
// ServiceOverridesPanel
// ---------------------------------------------------------------------------
export const ServiceOverridesPanel: React.FC<{
  /** Step 1's session key (config.previewTag) — prefills the Kafka splitting message filter's
   *  Baggage regex with this session's own actual value instead of a generic placeholder. */
  previewTag: string;
  serviceName: string;
  overrides: MirrordOverrides;
  availableDbTypes: DbBranchUiType[];
  kafkaAvailable: boolean;
  /** Pre-populated topic catalog for this service (DEVEX-819), if any. */
  kafkaTopics?: KafkaTopicCatalogEntry[];
  /** Parent whose Kafka split config this Kafka-consumer companion inherits, if any. */
  kafkaInheritedFrom?: string;
  /** Kafka-consumer companions that inherit this service's split config, if any. */
  kafkaAppliesTo?: string[];
  dbInheritedFrom?: string;
  dbAppliesTo?: string[];
  onChange: (ov: MirrordOverrides) => void;
  envVarNames?: string[];
  /** Values of DATABASE/DB_NAME/DBNAME/DB/SCHEMA-suffixed env vars, captured live from the running
   *  pod — keyed by env var name, used to prefill the "Database Name" field (see toggleDbType). */
  dbSchemaNames?: Record<string, string>;
  /** appmetadata.metadata.db_branches for this service, if any — used as a fallback for fields
   *  live env-var auto-detection can't resolve (see toggleDbType). */
  dbBranchOverrides?: Partial<Record<DbBranchUiType, DbBranchAppMetaOverride>>;
  dbTypeVersions?: Partial<Record<DbBranchUiType, string>>;
  /** Exact full image tag (e.g. "9.6.3") per DB type, for resolving a template's `image` field's
   *  $RANCH_INFERRED_VERSION — dbTypeVersions is truncated to major/major.minor, which 404s when
   *  spliced straight into a real Artifactory tag. */
  dbTypeFullTags?: Partial<Record<DbBranchUiType, string>>;
  /** Infra dependency name (e.g. "member-master-mysql") for each available DB type — lets
   *  matchEnvVarsToDbFields prefer env vars that specifically belong to this dependency over
   *  lookalike vars from another same-type instance sharing the namespace. */
  dbTypeDepNames?: Partial<Record<DbBranchUiType, string>>;
  /** True while the automatic schema-migrations check (GitHub-diff lookup) is in flight for this service. */
  migrationsCheckPending?: boolean;
}> = ({
  previewTag, serviceName, overrides, availableDbTypes, kafkaAvailable, kafkaTopics, kafkaInheritedFrom, kafkaAppliesTo = [], dbInheritedFrom, dbAppliesTo = [], onChange, envVarNames = [], dbSchemaNames = {}, dbBranchOverrides = {}, dbTypeVersions = {}, dbTypeFullTags = {},
  dbTypeDepNames = {}, migrationsCheckPending,
}) => {
  const [envOpen, setEnvOpen] = useState(false);
  const [dbOpen, setDbOpen] = useState(false);
  const [kafkaOpen, setKafkaOpen] = useState(false);

  const feature = overrides.feature ?? {};
  const envOverride: Record<string, string> = feature.env?.override ?? {};
  const dbBranches: DbBranchEntry[] = feature.db_branches ?? [];
  const splitQueues: Record<string, KafkaSplitEntry> = feature.split_queues ?? {};

  const dbAvailable = availableDbTypes.length > 0;
  const dbEnabled = dbAvailable && dbBranches.length > 0;
  const kafkaEnabled = kafkaAvailable && Object.keys(splitQueues).length > 0;

  // Env helpers
  const setEnvOverride = (kv: Record<string, string>) =>
    onChange({ ...overrides, feature: { ...feature, env: { ...(feature.env ?? {}), override: kv } } });

  const addEnvRow = () => { setEnvOpen(true); setEnvOverride({ ...envOverride, '': '' }); };

  const removeEnvRow = (key: string) => {
    const next = { ...envOverride };
    delete next[key];
    setEnvOverride(next);
  };

  const updateEnvKey = (oldKey: string, newKey: string) => {
    const val = envOverride[oldKey];
    const next = { ...envOverride };
    delete next[oldKey];
    next[newKey] = val;
    setEnvOverride(next);
  };

  const updateEnvVal = (key: string, val: string) => setEnvOverride({ ...envOverride, [key]: val });

  // DB helpers
  const toggleDbType = (type: DbBranchUiType) => {
    const existingIdx = dbBranches.findIndex(e => e.type === type);
    if (existingIdx >= 0) {
      const next = dbBranches.filter((_, i) => i !== existingIdx);
      if (next.length === 0) {
        const { db_branches: _o, ...rest } = feature;
        onChange({ ...overrides, feature: rest });
      } else {
        onChange({ ...overrides, feature: { ...feature, db_branches: next } });
      }
    } else {
      setDbOpen(true);
      // Live env-var auto-detection (dep-affinity-aware), then appmetadata env_var_overrides for
      // any field it can't resolve. See docs/development/endpoints/db-branch-config.md.
      const autoParams = envVarNames.length > 0 ? matchEnvVarsToDbFields(envVarNames, type, dbTypeDepNames[type]) : {};
      const appMetaOverride = dbBranchOverrides[type];
      const params: Record<string, DbBranchParamValue> = { ...autoParams };
      for (const [field, value] of Object.entries(appMetaOverride?.env_var_overrides ?? {})) {
        if (value !== undefined) params[field] = value;
      }
      // A template's `image` (e.g. a specific registry/flavor like percona vs vanilla mysql)
      // wins over `version` entirely — mirrord treats the two as mutually exclusive. Its
      // `$RANCH_INFERRED_VERSION` token resolves against the same live-detected version any
      // `version` field would otherwise use, so the tag still tracks the source automatically.
      const inferredVersion = dbTypeVersions[type] ?? DB_DEFAULT_VERSIONS[type];
      // The exact full tag, not inferredVersion's truncated major/major.minor form — that's
      // correct for the bare `version` field below (the operator maps it internally) but produces
      // a non-existent Artifactory tag when spliced into an `image` string (e.g. "9.6" from "9.6.3").
      const fullTag = dbTypeFullTags[type];
      // Only the templated form needs fullTag; a literal image must not be discarded for lack of one.
      const resolvedImage = appMetaOverride?.image
        ? (appMetaOverride.image.includes('$RANCH_INFERRED_VERSION')
            ? (fullTag ? appMetaOverride.image.replace('$RANCH_INFERRED_VERSION', fullTag) : undefined)
            : appMetaOverride.image)
        : undefined;

      const newEntry: DbBranchEntry = {
        // Auto-generated (PLATENG-1612) — previously left empty, requiring the user to type a
        // Branch ID manually before deploy. OverlayCandidatesStep's handleOverrideChange may
        // immediately overwrite this with a shared id if this dependency turns out to be shared
        // by a sibling candidate; otherwise it stands as-is.
        id: `${serviceName}-${type}-${crypto.randomUUID().slice(0, 8)}`,
        type,
        ...(resolvedImage
          ? { image: resolvedImage }
          // Explicit override wins over inference — same precedence as `name` below. Version gets
          // concatenated onto the registry to form the literal Redis image tag, so losing to a
          // guessed value the wrong way round would pull the wrong image outright, not just guess.
          : { version: appMetaOverride?.version ?? inferredVersion }),
        name: resolveDbBranchName(params, dbSchemaNames, type, appMetaOverride?.schema_name),
        ...(appMetaOverride?.profile ? { profile: appMetaOverride.profile } : {}),
        ...(appMetaOverride?.creation_timeout_secs ? { creation_timeout_secs: appMetaOverride.creation_timeout_secs } : {}),
        ...(appMetaOverride?.ttl_secs ? { ttl_secs: appMetaOverride.ttl_secs } : {}),
        ...(Object.keys(params).length > 0 ? { connection: { params } } : {}),
        // Full copy by default for all types (redis TLS SAN bug is fixed now).
        copy: {
          mode: appMetaOverride?.copy_mode ?? 'all',
          ...(appMetaOverride?.dump_args ? { dump_args: appMetaOverride.dump_args } : {}),
        },
      };
      onChange({ ...overrides, feature: { ...feature, db_branches: [...dbBranches, newEntry] } });

      // `migrations` is attached by OverlayCandidatesStep's branch-aware effect, not here — it
      // needs to re-check whenever the selected branch changes, not just on this one-shot toggle.
    }
  };

  const updateDbEntry = (idx: number, patch: Partial<DbBranchEntry>) => {
    const next = dbBranches.map((e, i) => (i === idx ? { ...e, ...patch } : e));
    onChange({ ...overrides, feature: { ...feature, db_branches: next } });
  };

  const updateDbConnectionParam = (idx: number, paramKey: string, value: DbBranchParamValue) => {
    const entry = dbBranches[idx];
    const params = { ...(entry.connection?.params ?? {}), [paramKey]: value };
    updateDbEntry(idx, { connection: { params } });
  };

  // Toggles a connection param between a plain env var name and a regex pattern extraction.
  const toggleParamPattern = (idx: number, paramKey: string) => {
    const entry = dbBranches[idx];
    const current = entry.connection?.params?.[paramKey];
    if (typeof current === 'object' && current !== null && 'value_pattern' in current) {
      updateDbConnectionParam(idx, paramKey, current.env_var_name);
    } else {
      // Switching a candidate-list value to pattern mode has no sensible 1:1 conversion —
      // starts blank, same as switching from an already-empty field.
      updateDbConnectionParam(idx, paramKey, { env_var_name: typeof current === 'string' ? current : '', value_pattern: '' });
    }
  };

  const updateDbCopyMode = (idx: number, mode: string) => {
    updateDbEntry(idx, { copy: mode ? { mode } : undefined });
  };

  // Schema migrations env editor — the only manually-editable part of migrations. Flavor,
  // image, command, and whether migrations apply at all are fully automatic (see toggleDbType
  // above); only container-flavor `env` is user-filled, since auto-populating it from the live
  // pod is an open security question (db-branching-env-var-audit.md finding #7).
  const migrationsEnvOf = (idx: number): Record<string, string> => {
    const m = dbBranches[idx]?.migrations;
    return (m?.flavor === 'container' ? m.env : undefined) ?? {};
  };

  const updateMigrationsEnv = (idx: number, env: Record<string, string>) => {
    const entry = dbBranches[idx];
    if (entry?.migrations?.flavor !== 'container') return;
    updateDbEntry(idx, { migrations: { ...entry.migrations, env } });
  };

  const addMigrationsEnvRow = (idx: number) => updateMigrationsEnv(idx, { ...migrationsEnvOf(idx), '': '' });

  const removeMigrationsEnvRow = (idx: number, key: string) => {
    const next = { ...migrationsEnvOf(idx) };
    delete next[key];
    updateMigrationsEnv(idx, next);
  };

  const updateMigrationsEnvKey = (idx: number, oldKey: string, newKey: string) => {
    const current = migrationsEnvOf(idx);
    const val = current[oldKey];
    const next = { ...current };
    delete next[oldKey];
    next[newKey] = val;
    updateMigrationsEnv(idx, next);
  };

  const updateMigrationsEnvVal = (idx: number, key: string, val: string) =>
    updateMigrationsEnv(idx, { ...migrationsEnvOf(idx), [key]: val });


  // Kafka helpers
  const toggleKafka = () => {
    if (!kafkaAvailable) return;
    if (kafkaEnabled) {
      const { split_queues: _o, ...rest } = feature;
      onChange({ ...overrides, feature: rest });
    } else {
      setKafkaOpen(true);
      // Prefill with this session's own Baggage value — the filter only ever needs to match
      // this session's traffic, so defaulting to it (instead of leaving it blank) means the
      // common case needs no manual typing at all.
      const baggage = previewTag ? `mirrord-session=${previewTag}` : '';
      // Default to the wildcard when a topic catalog exists (DEVEX-819); otherwise today's blank key.
      const topicKey = kafkaTopics && kafkaTopics.length > 0 ? '*' : '';
      onChange({ ...overrides, feature: { ...feature, split_queues: { [topicKey]: { queue_type: 'Kafka', message_filter: { baggage } } } } });
    }
  };

  const updateKafkaTopic = (oldTopic: string, newTopic: string, entry: KafkaSplitEntry) => {
    const next: Record<string, KafkaSplitEntry> = {};
    Object.entries(splitQueues).forEach(([k, v]) => {
      next[k === oldTopic ? newTopic : k] = v;
    });
    if (!Object.prototype.hasOwnProperty.call(next, newTopic)) next[newTopic] = entry;
    onChange({ ...overrides, feature: { ...feature, split_queues: next } });
  };

  // split_queues is a map keyed by topic name, so a second not-yet-named entry needs its own
  // unique placeholder key (an empty string would collide with an existing blank one) — updateKafkaTopic
  // renames whatever key is passed in once the user types a real topic name.
  const addKafkaTopic = () => {
    const baggage = previewTag ? `mirrord-session=${previewTag}` : '';
    const newKey = `__new-${crypto.randomUUID().slice(0, 8)}`;
    onChange({
      ...overrides,
      feature: { ...feature, split_queues: { ...splitQueues, [newKey]: { queue_type: 'Kafka', message_filter: { baggage } } } },
    });
  };

  const removeKafkaTopic = (topic: string) => {
    const { [topic]: _removed, ...rest } = splitQueues;
    if (Object.keys(rest).length === 0) {
      const { split_queues: _o, ...restFeature } = feature;
      onChange({ ...overrides, feature: restFeature });
    } else {
      onChange({ ...overrides, feature: { ...feature, split_queues: rest } });
    }
  };

  const updateKafkaFilter = (topic: string, baggage: string) =>
    onChange({
      ...overrides,
      feature: {
        ...feature,
        split_queues: {
          ...splitQueues,
          [topic]: { ...splitQueues[topic], message_filter: { baggage } },
        },
      },
    });

  const updateKafkaTopicEnvVar = (topic: string, topicEnvVar: string) =>
    onChange({
      ...overrides,
      feature: {
        ...feature,
        split_queues: {
          ...splitQueues,
          [topic]: { ...splitQueues[topic], topic_env_var: topicEnvVar || undefined },
        },
      },
    });

  const updateKafkaGroupIdEnvVar = (topic: string, groupIdEnvVar: string) =>
    onChange({
      ...overrides,
      feature: {
        ...feature,
        split_queues: {
          ...splitQueues,
          [topic]: { ...splitQueues[topic], group_id_env_var: groupIdEnvVar || undefined },
        },
      },
    });

  const { total, env: envChanged, db: dbChanged, kafka: kafkaChanged } = countOverrides(overrides);

  return (
    <div style={{ background: '#FAFBFC', border: `1px solid ${C.border}`, borderRadius: 6, margin: '0 0 12px 36px', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '8px 14px', background: '#F0F2F5', borderBottom: `1px solid ${C.border}`, fontSize: 13, color: C.muted }}>
        <span style={{ fontWeight: 600, color: '#172B4D' }}>Advanced options - {serviceName}</span>
        {total > 0 && <span style={changedBadge}>{total} changed</span>}
        {envChanged && <span style={badge(C.green, C.greenBg)}>Env</span>}
        {dbChanged && <span style={badge(C.blue, C.blueBg)}>DB</span>}
        {kafkaChanged && <span style={badge(C.kafkaBrown, C.kafkaBg)}>Kafka</span>}
      </div>

      {/* Env Variables */}
      <AccordionSection
        open={envOpen}
        onToggle={() => setEnvOpen(o => !o)}
        title="Env Variables"
        tag={Object.keys(envOverride).length > 0 ? <span style={badge(C.green, C.greenBg)}>+{Object.keys(envOverride).length} added</span> : null}
      >
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>
          Merged into <code>feature.env.override</code> for this service only.
        </div>
        {Object.entries(envOverride).map(([key, val], idx) => (
          <div key={idx} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
            <input style={key ? inputModified : inputBase} placeholder="KEY" value={key} onChange={e => updateEnvKey(key, e.target.value)} />
            <span style={{ color: C.muted, fontWeight: 700 }}>=</span>
            <input style={inputModified} placeholder="value" value={val} onChange={e => updateEnvVal(key, e.target.value)} />
            <button onClick={() => removeEnvRow(key)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#BF2600', fontSize: 16, lineHeight: 1 }} title="Remove">x</button>
          </div>
        ))}
        <button onClick={addEnvRow} style={{ background: C.blueBg, color: C.blue, border: `1px solid ${C.blue}`, borderRadius: 4, padding: '4px 10px', fontSize: 12, cursor: 'pointer', marginTop: 4 }}>
          + Add variable
        </button>
      </AccordionSection>

      {/* DB Branching */}
      <AccordionSection
        open={dbOpen}
        onToggle={() => setDbOpen(o => !o)}
        title="DB Branching"
        tag={!dbAvailable
          ? (dbInheritedFrom
              ? <span style={badge(C.blue, C.blueBg)}>🔗 Inherited from {dbInheritedFrom}</span>
              : <span style={badge(C.muted, '#EBECF0')}>Not available</span>)
          : dbBranches.length > 0
            ? <span style={badge(C.blue, C.blueBg)}>{dbBranches.length === 1 ? 'Enabled' : `Enabled (${dbBranches.length})`}</span>
            : null}
      >
        {!dbAvailable && dbInheritedFrom && (
          <div style={{ fontSize: 12, color: C.muted }}>
            Always mirrors "{dbInheritedFrom}"'s DB Branching config — no independent setting here.
          </div>
        )}
        {!dbAvailable && !dbInheritedFrom && (
          <div style={{ fontSize: 12, color: C.muted }}>
            Not available for this service. No direct MySQL/PostgreSQL/Mongo/Redis infra dependency was detected.
          </div>
        )}
        {dbAvailable && dbBranches.length > 0 && dbAppliesTo.length > 0 && (
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            🔗 Also applies to: {dbAppliesTo.map(name => (
              <span key={name} style={badge(C.blue, C.blueBg)}>{name}</span>
            ))}
          </div>
        )}
        {dbAvailable && (
          <>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>
              Select databases to branch for this service. Only databases with a detected infra dependency are shown.
            </div>
            {availableDbTypes.map(type => {
              const DB_TYPE_LABELS: Record<DbBranchUiType, string> = { mysql: 'MySQL', postgres: 'PostgreSQL', mongo: 'MongoDB', redis: 'Redis' };
              const entryIdx = dbBranches.findIndex(e => e.type === type);
              const isChecked = entryIdx >= 0;
              const entry = isChecked ? dbBranches[entryIdx] : null;
              return (
                <div key={type} style={{ marginBottom: 10, border: `1px solid ${isChecked ? C.blue : C.border}`, borderRadius: 6, overflow: 'hidden' }}>
                  <div
                    onClick={() => toggleDbType(type)}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: isChecked ? C.blueBg : C.bg, cursor: 'pointer' }}
                  >
                    <div style={toggleTrack(isChecked)}><div style={toggleKnob(isChecked)} /></div>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{DB_TYPE_LABELS[type]}</span>
                  </div>
                  {isChecked && entry && (
                    <div style={{ padding: '10px 12px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                        <div>
                          <div style={formLabel}>Branch ID</div>
                          <input style={inputModified} placeholder="e.g. users-mysql-db" value={entry.id} onChange={e => updateDbEntry(entryIdx, { id: e.target.value })} />
                        </div>
                        <div>
                          <div style={formLabel}>Version</div>
                          <input
                            style={{ ...inputModified, width: 120 }}
                            placeholder={type === 'redis' ? 'e.g. 7' : type === 'mongo' ? 'e.g. 6.0' : 'e.g. 5.7'}
                            value={entry.version ?? ''}
                            onChange={e => updateDbEntry(entryIdx, { version: e.target.value })}
                          />
                        </div>
                        {type !== 'redis' && (
                          <div>
                            <div style={formLabel}>Database Name</div>
                            <input style={inputModified} placeholder="e.g. teladoc_eds" value={entry.name ?? ''} onChange={e => updateDbEntry(entryIdx, { name: e.target.value })} />
                          </div>
                        )}
                      </div>
                      <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 10, marginTop: 4 }}>
                        <div style={{ fontSize: 12, color: C.muted, marginBottom: 8, fontStyle: 'italic' }}>
                          Connection params <span style={{ fontWeight: 600, color: '#BF2600' }}>*required</span>{' '}
                          <span style={{ fontWeight: 400 }}>— env var names from the running pod</span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                          {DB_FIELDS[type].map(field => {
                            const paramVal = entry.connection?.params?.[field.key];
                            const isList = Array.isArray(paramVal);
                            const isPattern = !isList && typeof paramVal === 'object' && paramVal !== null && 'value_pattern' in paramVal;
                            const isSet = isPattern
                              ? paramVal.env_var_name.trim().length > 0
                              : isList
                              ? paramVal.some(name => name.trim().length > 0)
                              : typeof paramVal === 'string' && paramVal.trim().length > 0;
                            return (
                              <div key={field.key}>
                                <div style={{ ...formLabel, display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <span>
                                    {field.label}
                                    {field.optional && <span style={{ fontWeight: 400, color: C.muted }}> (optional)</span>}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => toggleParamPattern(entryIdx, field.key)}
                                    style={{ marginLeft: 'auto', fontSize: 10, color: isPattern ? C.purple : C.muted, background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', fontWeight: isPattern ? 700 : 400 }}
                                    title="Toggle: plain env var name vs. regex pattern extraction from a composite env var (e.g. a JDBC URL)."
                                  >
                                    fx pattern
                                  </button>
                                </div>
                                {isPattern ? (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    <input
                                      style={paramVal.env_var_name ? inputModified : inputBase}
                                      placeholder="Env var name (e.g. CQS_ANALYTICS_DB_URL)"
                                      value={paramVal.env_var_name}
                                      onChange={e => updateDbConnectionParam(entryIdx, field.key, { env_var_name: e.target.value, value_pattern: paramVal.value_pattern })}
                                    />
                                    <input
                                      style={paramVal.value_pattern ? inputModified : inputBase}
                                      placeholder={`Regex with named group, e.g. (?P<${field.key}>[^:/]+)`}
                                      value={paramVal.value_pattern}
                                      onChange={e => updateDbConnectionParam(entryIdx, field.key, { env_var_name: paramVal.env_var_name, value_pattern: e.target.value })}
                                    />
                                  </div>
                                ) : (
                                  <input
                                    style={isSet ? inputModified : inputBase}
                                    placeholder={isList ? `${field.placeholder}, OTHER_CANDIDATE, ...` : field.placeholder}
                                    title={isList ? 'Comma-separated candidate env var names — the operator tries each in order and uses whichever exists on the pod.' : undefined}
                                    value={isList ? paramVal.join(', ') : typeof paramVal === 'string' ? paramVal : ''}
                                    onChange={e => {
                                      const raw = e.target.value;
                                      if (raw.includes(',')) {
                                        const candidates = raw.split(',').map(s => s.trim()).filter(Boolean);
                                        updateDbConnectionParam(entryIdx, field.key, candidates.length === 1 ? candidates[0] : candidates);
                                      } else {
                                        updateDbConnectionParam(entryIdx, field.key, raw);
                                      }
                                    }}
                                  />
                                )}
                              </div>
                            );
                          })}
                          <div>
                            <div style={formLabel}>Copy mode</div>
                            <select style={entry.copy?.mode ? selectModified : selectBase} value={entry.copy?.mode ?? ''} onChange={e => updateDbCopyMode(entryIdx, e.target.value)}>
                              {type === 'redis' ? (
                                <>
                                  <option value="">empty — no data (default)</option>
                                  <option value="all">all (full copy) — TLS-native redis (tas, eligibility, member_master, service-auth-gateway) can't reach Ready with this yet, see metalbear_redis_branch_image_and_flags.md</option>
                                </>
                              ) : (
                                <>
                                  <option value="">empty — no data/schema (default)</option>
                                  <option value="schema">schema only</option>
                                  <option value="all">all (full copy)</option>
                                </>
                              )}
                            </select>
                          </div>
                        </div>
                      </div>
                      {entry.migrations && (
                        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 10, marginTop: 10 }}>
                          <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>
                            <span style={badge(C.green, C.greenBg)}>Schema migrations</span>{' '}
                            will run automatically against this branch (migration file changes detected for this service).
                          </div>
                          <div style={{ fontSize: 12, color: C.muted, marginBottom: 6, fontStyle: 'italic' }}>
                            Env vars for the migration container (not auto-populated — add whatever this service's app needs to
                            boot, e.g. gathered via <code>kubectl exec ... printenv</code>). Reference the branch's own injected
                            connection details with <code>$(MIRRORD_DB_HOST)</code>, <code>$(MIRRORD_DB_PORT)</code>,{' '}
                            <code>$(MIRRORD_DB_USER)</code>, <code>$(MIRRORD_DB_PASSWORD)</code>, <code>$(MIRRORD_DB_NAME)</code>.
                          </div>
                          {Object.entries(migrationsEnvOf(entryIdx)).map(([key, val], idx) => (
                            <div key={idx} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                              <input style={key ? inputModified : inputBase} placeholder="KEY" value={key} onChange={e => updateMigrationsEnvKey(entryIdx, key, e.target.value)} />
                              <span style={{ color: C.muted, fontWeight: 700 }}>=</span>
                              <input style={inputModified} placeholder="value" value={val} onChange={e => updateMigrationsEnvVal(entryIdx, key, e.target.value)} />
                              <button onClick={() => removeMigrationsEnvRow(entryIdx, key)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#BF2600', fontSize: 16, lineHeight: 1 }} title="Remove">x</button>
                            </div>
                          ))}
                          <button onClick={() => addMigrationsEnvRow(entryIdx)} style={{ background: C.blueBg, color: C.blue, border: `1px solid ${C.blue}`, borderRadius: 4, padding: '4px 10px', fontSize: 12, cursor: 'pointer', marginTop: 4 }}>
                            + Add env var
                          </button>
                        </div>
                      )}
                      {!entry.migrations && migrationsCheckPending && (
                        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 10, marginTop: 10, fontSize: 12, color: C.muted, fontStyle: 'italic' }}>
                          Checking for migration file changes on this branch…
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </AccordionSection>

      {/* Kafka Splitting */}
      <AccordionSection
        open={kafkaOpen}
        onToggle={() => setKafkaOpen(o => !o)}
        title="Kafka Splitting"
        tag={kafkaAvailable
          ? (kafkaEnabled ? <span style={badge(C.kafkaBrown, C.kafkaBg)}>Enabled</span> : null)
          : kafkaInheritedFrom
            ? <span style={badge(C.kafkaBrown, C.kafkaBg)}>🔗 Inherited from {kafkaInheritedFrom}</span>
            : <span style={badge(C.muted, '#EBECF0')}>Not available</span>}
        last
      >
        {!kafkaAvailable && kafkaInheritedFrom && (
          <div style={{ fontSize: 12, color: C.muted }}>
            Always mirrors "{kafkaInheritedFrom}"'s Kafka Splitting config — no independent setting here.
          </div>
        )}
        {!kafkaAvailable && !kafkaInheritedFrom && (
          <div style={{ fontSize: 12, color: C.muted }}>
            Not available for this service. No direct Kafka infra dependency was detected.
          </div>
        )}
        {kafkaAvailable && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: kafkaAppliesTo.length > 0 ? 6 : 12 }}>
            <div style={toggleTrack(kafkaEnabled)} onClick={toggleKafka}><div style={toggleKnob(kafkaEnabled)} /></div>
            <span style={{ fontSize: 13 }}>Enable Kafka splitting for this service</span>
          </div>
        )}
        {kafkaAvailable && kafkaEnabled && kafkaAppliesTo.length > 0 && (
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            🔗 Also applies to: {kafkaAppliesTo.map(name => (
              <span key={name} style={badge(C.kafkaBrown, C.kafkaBg)}>{name}</span>
            ))}
          </div>
        )}
        {kafkaAvailable && kafkaEnabled && Object.entries(splitQueues).map(([topic, entry], idx) => (
          <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12, paddingBottom: 12, borderBottom: `1px solid ${C.border}` }}>
            <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => removeKafkaTopic(topic)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#BF2600', fontSize: 16, lineHeight: 1 }} title="Remove topic">x</button>
            </div>
            <div>
              <div style={formLabel}>Topic</div>
              <input style={inputModified} placeholder="e.g. member-events-topic" value={topic.startsWith('__new-') ? '' : topic} onChange={e => updateKafkaTopic(topic, e.target.value, entry)} />
            </div>
            <div>
              <div style={formLabel}>Queue Type</div>
              <select style={selectModified}><option>Kafka</option></select>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <div style={formLabel}>Message Filter (Baggage regex)</div>
              <input style={inputModified} placeholder="e.g. ^mirrord-session-.*$" value={entry.message_filter?.baggage ?? ''} onChange={e => updateKafkaFilter(topic, e.target.value)} />
            </div>
            <div>
              <div style={formLabel}>Topic Env Var <span style={{ fontWeight: 400, color: C.muted }}>(optional)</span></div>
              <input style={entry.topic_env_var ? inputModified : inputBase} placeholder="e.g. KAFKA_TOPIC" value={entry.topic_env_var ?? ''} onChange={e => updateKafkaTopicEnvVar(topic, e.target.value)} />
            </div>
            <div>
              <div style={formLabel}>Group ID Env Var <span style={{ fontWeight: 400, color: C.muted }}>(optional)</span></div>
              <input style={entry.group_id_env_var ? inputModified : inputBase} placeholder="e.g. KAFKA_GROUP_ID" value={entry.group_id_env_var ?? ''} onChange={e => updateKafkaGroupIdEnvVar(topic, e.target.value)} />
            </div>
          </div>
        ))}
        {kafkaAvailable && kafkaEnabled && (
          <button onClick={addKafkaTopic} style={{ background: C.blueBg, color: C.blue, border: `1px solid ${C.blue}`, borderRadius: 4, padding: '4px 10px', fontSize: 12, cursor: 'pointer', marginTop: 4 }}>
            + Add topic
          </button>
        )}
        {kafkaAvailable && !kafkaEnabled && <div style={{ fontSize: 12, color: C.muted }}>Tagged messages route to this preview pod. Untagged messages stay on the baseline consumer.</div>}
      </AccordionSection>
    </div>
  );
};

// ---------------------------------------------------------------------------
// InfraSection — collapsible infra list
// ---------------------------------------------------------------------------
export const InfraSection: React.FC<{
  infraServices: InfraService[];
  selectedInfra: PreviewService[];
  onInfraChange: (infra: PreviewService[]) => void;
}> = ({ infraServices, selectedInfra, onInfraChange }) => {
  const [open, setOpen] = useState(false);
  const selectedInfraNames = new Set(selectedInfra.map(s => s.name));

  const toggle = (infra: InfraService) => {
    if (selectedInfraNames.has(infra.name)) {
      onInfraChange(selectedInfra.filter(s => s.name !== infra.name));
    } else {
      onInfraChange([...selectedInfra, { name: infra.name, image: infra.currentImage ?? '', type: infra.type }]);
    }
  };

  if (infraServices.length === 0) return null;

  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 6, marginTop: 16 }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{ padding: '10px 14px', background: C.bg, borderBottom: open ? `1px solid ${C.border}` : 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none', fontSize: 13, fontWeight: 600 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, color: C.muted }}>{open ? '▼' : '▶'}</span>
          Available Infrastructure
          <span style={badge(C.blue, C.blueBg)}>{selectedInfra.length} / {infraServices.length}</span>
        </div>
        <span style={{ fontSize: 12, color: C.muted, fontWeight: 400 }}>Select infra services manually</span>
      </div>
      {open && (
        <div style={{ padding: '8px 0' }}>
          <Table aria-label="Infrastructure services" variant="compact">
            <Thead><Tr><Th /><Th>Service</Th><Th>Status</Th></Tr></Thead>
            <Tbody>
              {infraServices.map((infra, idx) => (
                <Tr key={infra.name} selected={selectedInfraNames.has(infra.name)}>
                  <Td select={{ rowIndex: idx, onSelect: () => toggle(infra), isSelected: selectedInfraNames.has(infra.name) }} />
                  <Td>{infra.name}</Td>
                  <Td>{infra.isRunning ? <Label color="green">running</Label> : <Label color="orange">stopped</Label>}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </div>
      )}
    </div>
  );
};
